<template>
    <div class="field-container-2">
        <div class="regular-type-header">
            {{header}}
        </div>
        <div v-if="fileField" class="file-field-data-container">
            <img v-if="isMediaCategoryImage" :src="fileField.value" alt=""
                 class="data-table-thumbnail">
            <font-awesome-icon v-else-if="isMediaCategoryAudio" :icon="['fal', 'file-audio']" size="2x"
                               class="category-icon"/>
            <font-awesome-icon v-else-if="isMediaCategoryPdf" :icon="['fal', 'file-pdf']" size="2x"
                               class="category-icon"/>
            <font-awesome-icon v-else-if="isMediaCategoryVideo" :icon="['fal', 'file-video']" size="2x"
                               class="category-icon"/>
            <a :href="fileField.value" target="_blank"
               class="file-field-link">{{fileField.value | lastOfUrl }}</a>
        </div>
        <label>
            <span class="custom-dropzone" v-on:dragover="handleDragOver" v-on:drop="handleDrop">
                <span v-if="selection && !allowsMultiple" class="selected-file-container">
                    <span>
                        <img v-if="isSelectedFileTypeImage" ref="thumbnail">
                        <font-awesome-icon v-else-if="isSelectedFileTypeAudio" :icon="['fal', 'file-audio']" size="2x"
                                           class="category-icon"/>
                        <font-awesome-icon v-else-if="isSelectedFileTypePdf" :icon="['fal', 'file-pdf']" size="2x"
                                           class="category-icon"/>
                        <font-awesome-icon v-else-if="isSelectedFileTypeVideo" :icon="['fal', 'file-video']" size="2x"
                                           class="category-icon"/>
                    </span>
                    <br>
                    <br>
                    <span>{{selection.name}}</span>
                </span>
                <span v-else-if="selectedFiles && selectedFiles.length > 0 && allowsMultiple"
                      class="selected-files-container">
                    <div v-for="datum in mappedSelectedFiles">
                        <div class="single-file-container">
                            <span>
                                <img v-if="datum.showImageThumbnail" :src="datum.item.src">
                                <font-awesome-icon v-if="datum.showIcon" :icon="datum.iconArray" size="2x"
                                                   class="category-icon"/>
                            </span>
                            <div class="info-container">
                                <span>{{datum.item.fileRef.name}}</span>
                                <div class="loading-indicator-container">
                                    <v-progress-circular v-if="datum.isProgress100" slot="loader"
                                                         :value="datum.item.progress"
                                                         :width="3" :rotate="-90"></v-progress-circular>
                                    <v-progress-circular v-else slot="loader" indeterminate
                                                         :width="3"></v-progress-circular>
                                </div>
                            </div>
                        </div>
                    </div>
                </span>
                <span v-else>{{dictDefaultMessage}}</span>
            </span>
            <input type="file" class="drop-zone-input" :multiple="allowsMultiple" :accept="acceptsString"
                   v-on:change="handleFileSelect">
        </label>
    </div>

</template>

<script>
    import {
        EVENT_INPUT,
        EVENT_TOUCHED,
        EVENT_VALIDATION_STATE,
        EVENT_FILE_SELECTED,
        ASSET_CATEGORY_IMAGE,
        ASSET_CATEGORY_AUDIO,
        ASSET_CATEGORY_PDF,
        ASSET_CATEGORY_VIDEO
    } from '../../services/constants'

    export default {
        name: 'sfr-file-selection-field',
        props: {
            existingAsset: Object,
            allowsMultiple: {
                type: Boolean,
                default: false,
            },
            stitch: {
                type: Boolean,
                default: false,
            },
            accepts: {
                type: Array,
                default: () => [],
            },
            selectedFiles: {
                type: Array,
                default: () => [],
            },
            inputValue: String,
        },
        model: {
            prop: 'inputValue',
            event: EVENT_INPUT
        },
        data() {
            return {
                selection: null,
            }
        },
        computed: {
            fileFieldType() {
                const obj = {
                    name: `File${this.allowsMultiple ? 's' : ''}`,
                    required: true
                }
                if (this.stitch) {
                    obj.name = 'Add additional files to be appended'
                }
                return obj
            },
            dictDefaultMessage() {
                return this.allowsMultiple ?
                    'Drag files here or click to select files' :
                    'Drag file here or click to select a file'
            },
            header() {
                return this.computeFieldHeader(this.fileFieldType)
            },
            isValid() {
                return this.valid
            },
            fileField() {
                if (this.existingAsset) {
                    return this.getUrlField(this.existingAsset.fields)
                } else {
                    return null
                }
            },
            mediaTypeField() {
                if (this.existingAsset) {
                    return this.getMediaTypeField(this.existingAsset.fields)
                } else {
                    return null
                }
            },
            mediaCategory() {
                if (this.mediaTypeField) {
                    const result = this.$store.state.media.mediaTypes.find(mediaType => {
                        return mediaType.mimeType === this.mediaTypeField.value
                    })
                    return result ? result.category : result
                } else {
                    return null
                }
            },
            isMediaCategoryImage() {
                return this.mediaCategory === ASSET_CATEGORY_IMAGE
            },
            isMediaCategoryAudio() {
                return this.mediaCategory === ASSET_CATEGORY_AUDIO
            },
            isMediaCategoryPdf() {
                return this.mediaCategory === ASSET_CATEGORY_PDF
            },
            isMediaCategoryVideo() {
                return this.mediaCategory === ASSET_CATEGORY_VIDEO
            },
            selectedFilesWithSrcs() {
                return this.selectedFiles.map(mediaObj => {
                    this.$set(mediaObj, 'src', null)
                    const reader = new FileReader();
                    reader.onload = (file) => {
                        mediaObj.src = file.target.result
                    }
                    reader.readAsDataURL(mediaObj.fileRef)
                    return mediaObj
                })
            },
            mappedSelectedFiles() {
                return this.selectedFilesWithSrcs.map(selectedFile => {
                    const showImageThumbnail = this.computeSelectedFileType(selectedFile.fileRef) === ASSET_CATEGORY_IMAGE
                        && !!selectedFile.src
                    const showAudioIcon = this.computeSelectedFileType(selectedFile.fileRef) === ASSET_CATEGORY_AUDIO
                    const showPdfIcon = this.computeSelectedFileType(selectedFile.fileRef) === ASSET_CATEGORY_PDF
                    const showVideoIcon = this.computeSelectedFileType(selectedFile.fileRef) === ASSET_CATEGORY_VIDEO
                    const showIcon = showAudioIcon || showPdfIcon || showVideoIcon

                    let iconName = ''
                    if (showAudioIcon) {
                        iconName = 'file-audio'
                    }
                    if (showPdfIcon) {
                        iconName = 'file-pdf'
                    }
                    if (showVideoIcon) {
                        iconName = 'file-video'
                    }

                    const iconArray = ['fal', iconName]

                    const isProgress100 = selectedFile.progress !== 100

                    return {
                        item: selectedFile,
                        showImageThumbnail,
                        showIcon,
                        iconArray,
                        isProgress100
                    }
                })
            },
            selectedFileType() {
                if (this.selection && !this.allowsMultiple) {
                    return this.computeSelectedFileType(this.selection)
                } else {
                    return null
                }
            },
            isSelectedFileTypeImage() {
                return this.selectedFileType === ASSET_CATEGORY_IMAGE
            },
            isSelectedFileTypeAudio() {
                return this.selectedFileType === ASSET_CATEGORY_AUDIO
            },
            isSelectedFileTypePdf() {
                return this.selectedFileType === ASSET_CATEGORY_PDF
            },
            isSelectedFileTypeVideo() {
                return this.selectedFileType === ASSET_CATEGORY_VIDEO
            },
            acceptsString() {
                let arr = []
                if (this.accepts.length > 0) {
                    arr = this.accepts
                } else {
                    arr = this.$store.state.media.mediaTypes.map(type => {
                        return type.mimeType
                    })
                }
                return arr.join(',')
            }
        },
        watch: {
            selectedFileType(newValue, oldValue) {
                if (newValue === ASSET_CATEGORY_IMAGE) {
                    const reader = new FileReader();
                    reader.onload = (file) => {
                        this.$refs['thumbnail'].src = file.target.result
                    }
                    reader.readAsDataURL(this.selection)
                }
            },
            '$route'() {
                this.selection = null
            },
        },
        methods: {
            handleFileSelect(evt) {
                const files = evt.target.files;
                if (this.allowsMultiple) {
                    this.onFileInputChange(this.filelistTofileArray(files))
                } else {
                    if (files[0]) {
                        this.onFileInputChange(files[0])
                    }
                }
            },
            handleDragOver(evt) {
                evt.stopPropagation();
                evt.preventDefault();
                evt.dataTransfer.dropEffect = 'copy'; // Explicitly show this is a copy.
            },
            handleDrop(evt) {
                evt.stopPropagation();
                evt.preventDefault();
                const files = evt.dataTransfer.files;
                if (this.allowsMultiple) {
                    this.onFileInputChange(this.filelistTofileArray(files))
                } else {
                    if (files[0]) {
                        this.onFileInputChange(files[0])
                    }
                }
            },
            computeFieldHeader(field) {
                return `${field.name}${field.required ? '* ' : ''}:`
            },
            emitInput($event) {
                this.$emit(EVENT_INPUT, $event)
                this.$emit(EVENT_TOUCHED, true)
            },
            inputHandler($event) {
                this.emitInput($event)
                this.validate()
            },
            validate() {
                let validationResult = false
                if (this.selection) {
                    validationResult = true
                }
                this.emitValidationState(validationResult)
            },

            emitValidationState($event) {
                this.$emit(EVENT_VALIDATION_STATE, $event)
            },
            onFileInputChange(fileObj) {
                this.selection = fileObj

                this.$emit(EVENT_FILE_SELECTED, fileObj)
                this.$emit(EVENT_TOUCHED, true)
                this.validate()
            },
            filelistTofileArray(filelist) {
                const arr = []
                for (let i = 0; i < filelist.length; i++) {
                    arr.push(filelist.item(i))
                }
                return arr
            },
            getUrlField(fields) {
                return fields.find(field => {
                    return field.identifier === 'url'
                })
            },
            getMediaTypeField(fields) {
                return fields.find(field => {
                    return field.identifier === 'media_type'
                })
            },
            computeSelectedFileType(fileObj) {
                if (fileObj.type.match('image.*')) {
                    return ASSET_CATEGORY_IMAGE
                } else if (fileObj.type.match('audio.*')) {
                    return ASSET_CATEGORY_AUDIO
                } else if (fileObj.type.match('video.*')) {
                    return ASSET_CATEGORY_VIDEO
                } else if (fileObj.type.match('application/pdf')) {
                    return ASSET_CATEGORY_PDF
                } else {
                    return null
                }
            },
            computeSrc(evt) {
                console.log(this.$refs.list)
                // if (newValue === 'Image') {
                //     const reader = new FileReader();
                //     reader.onload = (file) => {
                //         this.$refs['thumbnail'].src = file.target.result
                //     }
                //     reader.readAsDataURL(this.selection)
                // }
            },
            reset() {
                this.selection = null
            }
        },
        filters: {
            lastOfUrl(urlStr) {
                const urlParts = urlStr.split('/')
                return urlParts[urlParts.length - 1]
            }
        },
        created() {
            this.validate()
        },
    }
</script>

<style scoped lang="scss">
    @import "../../meta";

    .field-container-1 {
        margin: 2em 0;

        .field-container-2 {
            padding: 0 1em;
            $border-width: 3px;
            border-left: $border-width solid $accent;
            border-radius: $border-width;
        }
    }

    .regular-type-header {
        font-size: 14px;

        .input-group {
            padding: 0px;
        }
    }

    .file-field-data-container {
        margin: 1em 0;

        .data-table-thumbnail {
            $size: 30px;
            max-height: $size;
            max-width: $size;
            vertical-align: middle;
            margin: 0 1em 0 0;
        }

        .category-icon {
            margin: 0 .5em 0 0;
        }

        a.file-field-link {
            color: blue;
        }
    }

    .custom-dropzone {
        background: white;
        border: rgb(229, 229, 229) solid 2px;
        transition: background-color .2s linear;
        color: #777;
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 200px;
        cursor: pointer;

        &:hover {
            background: #f6f6f6;
        }

        .selected-file-container {
            border: rgb(229, 229, 229) solid 2px;
            border-radius: 5px;
            padding: 5px;
            margin: 5px;
            display: flex;
            flex-direction: column;
            width: 100%;
        }

        .selected-files-container {
            padding: 5px;
            display: flex;
            flex-direction: column;
            width: 100%;
        }

        img {
            $size: 130px;
            max-height: $size;
            max-width: $size;
            vertical-align: middle;
            margin: 0 1em 0 0;
        }

    }

    .single-file-container {
        margin: 10px;
        border: rgb(229, 229, 229) solid 2px;
        border-radius: 5px;
        padding: 5px;

    }

    .info-container {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin: 5px 0 0 0;
    }

    .loading-indicator-container {
        margin: 0 0 0 10px;
        display: flex;
        align-items: center;
    }

    .drop-zone-input {
        display: none;
    }

</style>
